import React, { useState, useEffect } from 'react';
import { getCurrencySymbol, formatCurrency } from '../../utils/currency';
import axios from 'axios';
import { API, useAuth } from '../../App';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  FileText,
  Receipt,
  BookOpen,
  Lock,
  Download,
  ChevronRight,
  ArrowUpRight,
  RefreshCw,
  Landmark,
  TrendingUp,
  Wallet,
  Building,
  Calculator,
  Clock,
  Zap,
  Globe,
  Shield,
} from 'lucide-react';
import { format } from 'date-fns';

/**
 * Comprehensive source type -> navigation route mapping.
 * Covers every possible journal entry source_type in the system.
 */
const SOURCE_ROUTE_MAP = {
  // --- Invoices ---
  invoice: { route: (id) => `/invoices/${id}`, label: 'Invoice', icon: FileText, color: 'text-green-600', badge: 'bg-green-50 text-green-700 border-green-200' },
  invoice_recognition: { route: (id) => `/invoices/${id}`, label: 'Invoice', icon: FileText, color: 'text-green-600', badge: 'bg-green-50 text-green-700 border-green-200' },
  invoice_issued: { route: (id) => `/invoices/${id}`, label: 'Invoice', icon: FileText, color: 'text-green-600', badge: 'bg-green-50 text-green-700 border-green-200' },
  invoice_payment: { route: (id) => `/invoices/${id}`, label: 'Payment In', icon: ArrowUpRight, color: 'text-green-600', badge: 'bg-green-50 text-green-700 border-green-200' },
  invoice_paid: { route: (id) => `/invoices/${id}`, label: 'Payment In', icon: ArrowUpRight, color: 'text-green-600', badge: 'bg-green-50 text-green-700 border-green-200' },
  payment_received: { route: (id) => `/invoices/${id}`, label: 'Payment In', icon: ArrowUpRight, color: 'text-green-600', badge: 'bg-green-50 text-green-700 border-green-200' },
  invoice_reversal: { route: (id) => `/invoices/${id}`, label: 'Void Invoice', icon: RefreshCw, color: 'text-red-500', badge: 'bg-red-50 text-red-700 border-red-200' },
  invoice_edit_reversal: { route: (id) => `/invoices/${id}`, label: 'Invoice Edit', icon: RefreshCw, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },

  // --- Bills ---
  bill: { route: (id) => `/bills/${id}`, label: 'Bill', icon: Receipt, color: 'text-red-600', badge: 'bg-red-50 text-red-700 border-red-200' },
  bill_recognition: { route: (id) => `/bills/${id}`, label: 'Bill', icon: Receipt, color: 'text-red-600', badge: 'bg-red-50 text-red-700 border-red-200' },
  bill_issued: { route: (id) => `/bills/${id}`, label: 'Bill', icon: Receipt, color: 'text-red-600', badge: 'bg-red-50 text-red-700 border-red-200' },
  bill_created: { route: (id) => `/bills/${id}`, label: 'Bill', icon: Receipt, color: 'text-red-600', badge: 'bg-red-50 text-red-700 border-red-200' },
  bill_payment: { route: (id) => `/bills/${id}`, label: 'Payment Out', icon: ArrowUpRight, color: 'text-red-600', badge: 'bg-red-50 text-red-700 border-red-200' },
  bill_paid: { route: (id) => `/bills/${id}`, label: 'Payment Out', icon: ArrowUpRight, color: 'text-red-600', badge: 'bg-red-50 text-red-700 border-red-200' },
  payment_made: { route: (id) => `/bills/${id}`, label: 'Payment Out', icon: ArrowUpRight, color: 'text-red-600', badge: 'bg-red-50 text-red-700 border-red-200' },
  bill_reversal: { route: (id) => `/bills/${id}`, label: 'Void Bill', icon: RefreshCw, color: 'text-red-500', badge: 'bg-red-50 text-red-700 border-red-200' },
  bill_edit_reversal: { route: (id) => `/bills/${id}`, label: 'Bill Edit', icon: RefreshCw, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  prepaid_expense: { route: (id) => `/bills/${id}`, label: 'Prepaid', icon: Clock, color: 'text-purple-600', badge: 'bg-purple-50 text-purple-700 border-purple-200' },

  // --- Payments ---
  partial_payment: { route: (id) => `/invoices/${id}`, label: 'Payment', icon: ArrowUpRight, color: 'text-green-600', badge: 'bg-green-50 text-green-700 border-green-200' },
  payment_reversal: { route: (id) => `/invoices/${id}`, label: 'Payment Void', icon: RefreshCw, color: 'text-red-500', badge: 'bg-red-50 text-red-700 border-red-200' },
  payment_void: { route: (id) => `/invoices/${id}`, label: 'Payment Void', icon: RefreshCw, color: 'text-red-500', badge: 'bg-red-50 text-red-700 border-red-200' },

  // --- Other Receipts / Payments (Bank Feed) ---
  other_receipt: { route: (id) => id ? `/other-receipts/${id}` : `/bank-feeds?tab=transactions&filter=processed`, label: 'Receipt', icon: ArrowUpRight, color: 'text-green-600', badge: 'bg-green-50 text-green-700 border-green-200' },
  other_receipt_reversal: { route: (id) => id ? `/other-receipts/${id}` : `/bank-feeds?tab=transactions&filter=processed`, label: 'Receipt Void', icon: RefreshCw, color: 'text-red-500', badge: 'bg-red-50 text-red-700 border-red-200' },
  other_payment: { route: (id) => id ? `/other-payments/${id}` : `/bank-feeds?tab=transactions&filter=processed`, label: 'Payment', icon: ArrowUpRight, color: 'text-red-600', badge: 'bg-red-50 text-red-700 border-red-200' },
  other_payment_reversal: { route: (id) => id ? `/other-payments/${id}` : `/bank-feeds?tab=transactions&filter=processed`, label: 'Payment Void', icon: RefreshCw, color: 'text-red-500', badge: 'bg-red-50 text-red-700 border-red-200' },

  // --- Special Items ---
  loan_received: { route: () => `/loans`, label: 'Loan', icon: Landmark, color: 'text-blue-600', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  loan_receipt: { route: () => `/loans`, label: 'Loan', icon: Landmark, color: 'text-blue-600', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  investment_made: { route: () => `/investments`, label: 'Investment', icon: TrendingUp, color: 'text-emerald-600', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  investment_purchase: { route: () => `/investments`, label: 'Investment', icon: TrendingUp, color: 'text-emerald-600', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  fixed_asset: { route: () => `/fixed-assets`, label: 'Fixed Asset', icon: Building, color: 'text-slate-600', badge: 'bg-slate-50 text-slate-700 border-slate-200' },
  fixed_asset_purchase: { route: () => `/fixed-assets`, label: 'Fixed Asset', icon: Building, color: 'text-slate-600', badge: 'bg-slate-50 text-slate-700 border-slate-200' },
  prepayment: { route: () => `/prepaid-expenses`, label: 'Prepayment', icon: Clock, color: 'text-purple-600', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  prepayment_purchase: { route: () => `/prepaid-expenses`, label: 'Prepayment', icon: Clock, color: 'text-purple-600', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  deferred_revenue: { route: () => `/deferred-revenue`, label: 'Deferred Rev.', icon: Clock, color: 'text-indigo-600', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  deferral_schedule: { route: () => `/deferred-revenue`, label: 'Deferred Rev.', icon: Clock, color: 'text-indigo-600', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' },

  // --- Credit / Debit Notes ---
  credit_note: { route: () => `/invoices`, label: 'Credit Note', icon: FileText, color: 'text-orange-600', badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  sales_credit_note: { route: () => `/invoices`, label: 'Credit Note', icon: FileText, color: 'text-orange-600', badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  debit_note: { route: () => `/bills`, label: 'Debit Note', icon: Receipt, color: 'text-orange-600', badge: 'bg-orange-50 text-orange-700 border-orange-200' },
  vendor_debit_note: { route: () => `/bills`, label: 'Debit Note', icon: Receipt, color: 'text-orange-600', badge: 'bg-orange-50 text-orange-700 border-orange-200' },

  // --- Accrual Engine ---
  customer_prepayment: { route: () => `/invoices`, label: 'Customer Prepay', icon: Wallet, color: 'text-green-600', badge: 'bg-green-50 text-green-700 border-green-200' },
  supplier_prepayment: { route: () => `/bills`, label: 'Supplier Prepay', icon: Wallet, color: 'text-red-600', badge: 'bg-red-50 text-red-700 border-red-200' },
  customer_refund: { route: () => `/invoices`, label: 'Refund', icon: RefreshCw, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  customer_overpayment: { route: () => `/invoices`, label: 'Overpayment', icon: Wallet, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  credit_application: { route: () => `/invoices`, label: 'Credit Applied', icon: FileText, color: 'text-green-600', badge: 'bg-green-50 text-green-700 border-green-200' },
  internal_transfer: { route: () => `/banks`, label: 'Transfer', icon: RefreshCw, color: 'text-blue-600', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  owner_capital: { route: () => `/manual-journals`, label: 'Capital', icon: Landmark, color: 'text-purple-600', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  owner_withdrawal: { route: () => `/manual-journals`, label: 'Drawings', icon: Wallet, color: 'text-purple-600', badge: 'bg-purple-50 text-purple-700 border-purple-200' },

  // --- Manual Journals ---
  manual: { route: (id) => `/manual-journals?entry=${id}`, label: 'Journal', icon: BookOpen, color: 'text-blue-600', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  journal_entry: { route: (id) => `/manual-journals?entry=${id}`, label: 'Journal', icon: BookOpen, color: 'text-blue-600', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  manual_journal: { route: (id) => `/manual-journals?entry=${id}`, label: 'Journal', icon: BookOpen, color: 'text-blue-600', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  manual_journal_edit_reversal: { route: (id) => `/manual-journals?entry=${id}`, label: 'Journal Edit', icon: RefreshCw, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  reversal: { route: (id) => `/manual-journals?entry=${id}`, label: 'Reversal', icon: RefreshCw, color: 'text-red-500', badge: 'bg-red-50 text-red-700 border-red-200' },
  recurring: { route: (id) => `/manual-journals?entry=${id}`, label: 'Recurring', icon: RefreshCw, color: 'text-blue-600', badge: 'bg-blue-50 text-blue-700 border-blue-200' },

  // --- Automated Scheduler Entries ---
  depreciation: { route: () => `/fixed-assets`, label: 'Depreciation', icon: Calculator, color: 'text-slate-500', badge: 'bg-slate-50 text-slate-600 border-slate-200', system: true },
  depreciation_monthly: { route: () => `/fixed-assets`, label: 'Depreciation', icon: Calculator, color: 'text-slate-500', badge: 'bg-slate-50 text-slate-600 border-slate-200', system: true },
  amortization: { route: () => `/prepaid-expenses`, label: 'Amortization', icon: Calculator, color: 'text-purple-500', badge: 'bg-purple-50 text-purple-600 border-purple-200', system: true },
  amortization_monthly: { route: () => `/prepaid-expenses`, label: 'Amortization', icon: Calculator, color: 'text-purple-500', badge: 'bg-purple-50 text-purple-600 border-purple-200', system: true },
  loan_interest_accrual: { route: () => `/loans`, label: 'Interest Accrual', icon: Calculator, color: 'text-blue-500', badge: 'bg-blue-50 text-blue-600 border-blue-200', system: true },
  investment_income_accrual: { route: () => `/investments`, label: 'Income Accrual', icon: Calculator, color: 'text-emerald-500', badge: 'bg-emerald-50 text-emerald-600 border-emerald-200', system: true },
  deferred_revenue_recognition: { route: () => `/deferred-revenue`, label: 'Rev. Recog.', icon: Calculator, color: 'text-indigo-500', badge: 'bg-indigo-50 text-indigo-600 border-indigo-200', system: true },
  fx_revaluation_unrealized: { route: () => `/reports`, label: 'FX Reval.', icon: Globe, color: 'text-teal-500', badge: 'bg-teal-50 text-teal-600 border-teal-200', system: true },

  // --- Tax Detection ---
  government: { route: null, label: 'Tax Update', icon: Shield, color: 'text-gray-500', badge: 'bg-gray-50 text-gray-600 border-gray-200', system: true },

  // --- Opening Balances ---
  opening_balance: { route: () => `/data-migration?tab=audit`, label: 'Opening Bal.', icon: Zap, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  opening_balance_receivable: { route: (id) => `/invoices/${id}`, label: 'Opening AR', icon: Zap, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  opening_balance_payable: { route: (id) => `/bills/${id}`, label: 'Opening AP', icon: Zap, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  opening_balance_fixed_asset: { route: () => `/fixed-assets`, label: 'Opening FA', icon: Zap, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  opening_balance_prepayment: { route: () => `/prepaid-expenses`, label: 'Opening Prepay', icon: Zap, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  opening_balance_loan: { route: () => `/loans`, label: 'Opening Loan', icon: Zap, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  opening_balance_investment: { route: () => `/investments`, label: 'Opening Invest.', icon: Zap, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  opening_balance_deferred_revenue: { route: () => `/deferred-revenue`, label: 'Opening Def. Rev.', icon: Zap, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  opening_balance_other_asset: { route: () => `/data-migration?tab=audit`, label: 'Opening Asset', icon: Zap, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  opening_balance_other_liability: { route: () => `/data-migration?tab=audit`, label: 'Opening Liab.', icon: Zap, color: 'text-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },

  // --- Bank Feed / Open Banking (read-only) ---
  open_banking: { route: null, label: 'Bank Import', icon: Lock, color: 'text-gray-400', badge: 'bg-gray-50 text-gray-600 border-gray-200', system: true, readOnly: true },
  mono: { route: null, label: 'Bank Import', icon: Lock, color: 'text-gray-400', badge: 'bg-gray-50 text-gray-600 border-gray-200', system: true, readOnly: true },
  bank_feed: { route: () => `/banks`, label: 'Bank Feed', icon: Landmark, color: 'text-blue-500', badge: 'bg-blue-50 text-blue-600 border-blue-200' },
  bank_fee: { route: () => `/banks`, label: 'Bank Charge', icon: Landmark, color: 'text-gray-500', badge: 'bg-gray-50 text-gray-600 border-gray-200' },
  bank_charge: { route: () => `/banks`, label: 'Bank Charge', icon: Landmark, color: 'text-gray-500', badge: 'bg-gray-50 text-gray-600 border-gray-200' },
  bank_interest: { route: () => `/banks`, label: 'Bank Interest', icon: Landmark, color: 'text-blue-500', badge: 'bg-blue-50 text-blue-600 border-blue-200' },
  import: { route: null, label: 'Import', icon: FileText, color: 'text-gray-500', badge: 'bg-gray-50 text-gray-600 border-gray-200', system: true },
};

const GLDrilldownModal = ({
  open,
  onOpenChange,
  glCode,
  accountId,
  accountName,
  startDate,
  endDate,
  activeReport,
  onTransactionClick
}) => {
  const { business } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const formatAmount = (amount) => formatCurrency(amount, business?.currency || 'NGN');

  useEffect(() => {
    if (open && (glCode || accountId)) {
      fetchBreakdown();
    }
  }, [open, glCode, accountId, startDate, endDate]);

  const fetchBreakdown = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (glCode) params.append('gl_code', glCode);
      if (accountId) params.append('account_id', accountId);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const response = await axios.get(`${API}/reports/gl-breakdown?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load GL breakdown:', error);
      toast.error('Failed to load transaction breakdown');
    } finally {
      setLoading(false);
    }
  };

  const handleTransactionClick = (transaction) => {
    if (onTransactionClick) {
      onTransactionClick(transaction);
      return;
    }

    const sourceType = transaction.source_document_type || transaction.source_type;
    const sourceId = transaction.source_document_id || transaction.source_id;
    const mapping = SOURCE_ROUTE_MAP[sourceType];

    // Read-only bank imports
    if (mapping?.readOnly || transaction.is_system_generated) {
      if (mapping?.route) {
        navigate(mapping.route(sourceId));
        return;
      }
      toast.info('This is a system-generated entry');
      return;
    }

    if (!mapping || !mapping.route) {
      toast.info(`Cannot navigate to "${sourceType}" entry`);
      return;
    }

    const route = mapping.route(sourceId);
    if (!route) {
      toast.info('Source document details not available');
      return;
    }

    // Build return URL for browser back button support
    const returnParams = new URLSearchParams();
    if (glCode) returnParams.set('drilldown_gl', glCode);
    if (accountId) returnParams.set('drilldown_id', accountId);
    if (accountName) returnParams.set('drilldown_name', encodeURIComponent(accountName));
    if (activeReport) returnParams.set('report', activeReport);
    const returnUrl = `/reports?${returnParams.toString()}`;

    navigate(route, {
      state: { returnUrl, returnLabel: 'Reports' }
    });
  };

  const getSourceBadge = (transaction) => {
    const sourceType = transaction.source_type;
    const mapping = SOURCE_ROUTE_MAP[sourceType];

    if (!mapping) {
      return (
        <Badge variant="outline" className="text-xs bg-gray-50 text-gray-600">
          Entry
        </Badge>
      );
    }

    const Icon = mapping.icon;
    const isClickable = mapping.route && !mapping.readOnly;

    return (
      <Badge variant="outline" className={`text-xs ${mapping.badge} ${isClickable ? '' : 'opacity-70'}`}>
        <Icon className={`w-3 h-3 mr-1 ${mapping.color}`} />
        {mapping.label}
      </Badge>
    );
  };

  const isClickable = (transaction) => {
    const sourceType = transaction.source_document_type || transaction.source_type;
    const mapping = SOURCE_ROUTE_MAP[sourceType];
    return mapping?.route && !mapping?.readOnly;
  };

  const downloadCSV = () => {
    if (!data?.transactions) return;
    const headers = ['Date', 'Description', 'Reference', 'Type', 'Debit', 'Credit', 'Balance'];
    const rows = data.transactions.map(t => [
      t.date,
      `"${(t.description || '').replace(/"/g, '""')}"`,
      t.reference || '',
      SOURCE_ROUTE_MAP[t.source_type]?.label || t.source_type || '',
      t.debit || '',
      t.credit || '',
      t.running_balance || ''
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GL_${data.account?.code || 'breakdown'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded CSV');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="gl-drilldown-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#283F8B]" />
            {data?.account?.name || accountName || 'Transaction Breakdown'}
          </DialogTitle>
          <DialogDescription>
            {data?.account?.code && (
              <span className="font-mono text-[#283F8B]">{data.account.code}</span>
            )}
            {' '}
            {startDate && endDate && (
              <span className="text-muted-foreground">
                | {format(new Date(startDate), 'MMM d, yyyy')} - {format(new Date(endDate), 'MMM d, yyyy')}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#283F8B]" />
          </div>
        ) : data ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Opening Balance Banner */}
            {data.summary?.opening_balance !== undefined && data.summary.opening_balance !== 0 && (
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-3">
                <span className="text-sm font-medium text-amber-800">Opening Balance (as at {startDate ? new Date(startDate).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'}) : 'start'})</span>
                <span className="text-base font-bold text-amber-900 font-mono">{formatAmount(data.summary.opening_balance)}</span>
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-5 gap-3 mb-4">
              {data.summary?.opening_balance !== undefined && (
                <div className="bg-amber-50 rounded-lg p-3">
                  <div className="text-xs text-amber-600 font-medium">Opening Balance</div>
                  <div className="text-lg font-bold text-amber-700">{formatAmount(data.summary.opening_balance)}</div>
                </div>
              )}
              <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-xs text-blue-600 font-medium">Total Debits</div>
                <div className="text-lg font-bold text-blue-700">{formatAmount(data.summary?.total_debits)}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <div className="text-xs text-green-600 font-medium">Total Credits</div>
                <div className="text-lg font-bold text-green-700">{formatAmount(data.summary?.total_credits)}</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-3">
                <div className="text-xs text-purple-600 font-medium">Closing Balance</div>
                <div className="text-lg font-bold text-purple-700">{formatAmount(data.summary?.closing_balance)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-600 font-medium">Transactions</div>
                <div className="text-lg font-bold text-gray-700">{data.summary?.transaction_count || 0}</div>
              </div>
            </div>

            {/* Transaction Table */}
            <div className="flex-1 overflow-auto border rounded-lg">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="w-[100px]">Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[120px]">Source</TableHead>
                    <TableHead className="text-right w-[120px]">Debit</TableHead>
                    <TableHead className="text-right w-[120px]">Credit</TableHead>
                    <TableHead className="text-right w-[120px]">Balance</TableHead>
                    <TableHead className="w-[40px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.transactions?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No transactions found for this period
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.transactions?.map((transaction, index) => {
                      const clickable = isClickable(transaction);
                      return (
                        <TableRow
                          key={transaction.id || index}
                          className={`${clickable ? 'cursor-pointer hover:bg-muted/50' : ''} ${transaction.is_system_generated ? 'bg-gray-50/50' : ''}`}
                          onClick={() => handleTransactionClick(transaction)}
                          data-testid={`gl-transaction-row-${index}`}
                        >
                          <TableCell className="font-mono text-sm">
                            {transaction.date ? format(new Date(transaction.date), 'MMM d') : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="text-sm line-clamp-1">{transaction.description || 'No description'}</span>
                              {transaction.reference && (
                                <span className="text-xs text-muted-foreground">Ref: {transaction.reference}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{getSourceBadge(transaction)}</TableCell>
                          <TableCell className="text-right font-mono">
                            {transaction.debit > 0 && <span className="text-blue-600">{formatAmount(transaction.debit)}</span>}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {transaction.credit > 0 && <span className="text-green-600">{formatAmount(transaction.credit)}</span>}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatAmount(transaction.running_balance)}</TableCell>
                          <TableCell>
                            {clickable && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                            {!clickable && transaction.is_system_generated && <Lock className="w-3.5 h-3.5 text-gray-400" />}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center pt-4 border-t mt-4">
              <div className="text-sm text-muted-foreground">
                Click on a row to view the source transaction
              </div>
              <Button variant="outline" size="sm" onClick={downloadCSV}>
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">No data available</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GLDrilldownModal;
